import { Component } from '../Component';

/**
 * Record of a taming attempt
 */
export interface TamingAttempt {
  timestamp: number;
  itemId: string;
  loyaltyGain: number;
}

/**
 * TamingComponent - Tracks taming progress for wild beasties
 * Used for the loyalty/feeding mechanic to befriend wild beasties
 */
export class TamingComponent extends Component {
  public readonly type = 'taming';

  /** Whether this beastie is currently being tamed */
  public isBeingTamed: boolean;

  /** Player ID attempting to tame */
  public tamerId: string;

  /** Current taming progress (accumulated loyalty) */
  public tamingProgress: number;

  /** Threshold to capture (typically 50-75) */
  public captureThreshold: number;

  /** History of feeding/taming attempts */
  public attempts: TamingAttempt[];

  /** Time of last interaction */
  public lastInteractionTime: number;

  /** Cooldown between feeding (ms) */
  public feedCooldownMs: number;

  /** Decay rate per second when not interacted with */
  public loyaltyDecayRate: number;

  /** Whether beastie has fled from taming */
  public hasFled: boolean;

  constructor(data?: Partial<TamingComponent>) {
    super();
    this.isBeingTamed = data?.isBeingTamed ?? false;
    this.tamerId = data?.tamerId ?? '';
    this.tamingProgress = data?.tamingProgress ?? 0;
    this.captureThreshold = data?.captureThreshold ?? 60;
    this.attempts = data?.attempts ?? [];
    this.lastInteractionTime = data?.lastInteractionTime ?? 0;
    this.feedCooldownMs = data?.feedCooldownMs ?? 2000;
    this.loyaltyDecayRate = data?.loyaltyDecayRate ?? 1; // 1 per second
    this.hasFled = data?.hasFled ?? false;
  }

  /**
   * Check if can feed again (cooldown elapsed)
   */
  public canFeed(currentTime: number): boolean {
    return currentTime - this.lastInteractionTime >= this.feedCooldownMs;
  }

  /**
   * Get remaining cooldown in ms
   */
  public getRemainingCooldown(currentTime: number): number {
    const elapsed = currentTime - this.lastInteractionTime;
    return Math.max(0, this.feedCooldownMs - elapsed);
  }

  /**
   * Add loyalty from feeding
   */
  public addLoyalty(amount: number, itemId: string, currentTime: number): void {
    this.tamingProgress = Math.min(100, this.tamingProgress + amount);
    this.lastInteractionTime = currentTime;
    this.attempts.push({
      timestamp: currentTime,
      itemId,
      loyaltyGain: amount,
    });
  }

  /**
   * Check if can be captured (threshold reached)
   */
  public canCapture(): boolean {
    return this.tamingProgress >= this.captureThreshold;
  }

  /**
   * Get progress as percentage (0-1)
   */
  public getProgressPercent(): number {
    return this.tamingProgress / this.captureThreshold;
  }

  /**
   * Apply decay over time when not being fed
   */
  public applyDecay(deltaSeconds: number): void {
    if (this.tamingProgress > 0) {
      this.tamingProgress = Math.max(0, this.tamingProgress - this.loyaltyDecayRate * deltaSeconds);
    }
  }

  /**
   * Reset taming state (e.g., when beastie flees)
   */
  public reset(): void {
    this.isBeingTamed = false;
    this.tamerId = '';
    this.tamingProgress = 0;
    this.attempts = [];
    this.lastInteractionTime = 0;
  }
}
